import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Clock, TrendingUp, Eye, CheckSquare, ChevronDown, ChevronRight, ListFilter } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [byType, setByType] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [tasksMetrics, setTasksMetrics] = useState(null);
  const [allDeadlines, setAllDeadlines] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [showDeadlineModal, setShowDeadlineModal] = useState(false);
  const [deadlineFilter, setDeadlineFilter] = useState('Todos');
  const [taskFilter, setTaskFilter] = useState('Todos');
  const [expandedDeadlineClients, setExpandedDeadlineClients] = useState({});
  const [expandedTaskClients, setExpandedTaskClients] = useState({});

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      const [metricsRes, byTypeRes, activityRes, tasksMetricsRes, deadlinesRes, tasksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard/metrics`),
        axios.get(`${BACKEND_URL}/api/dashboard/by-type`),
        axios.get(`${BACKEND_URL}/api/dashboard/recent-activity`),
        axios.get(`${BACKEND_URL}/api/dashboard/tasks-metrics`),
        axios.get(`${BACKEND_URL}/api/deadlines`),
        axios.get(`${BACKEND_URL}/api/tasks`),
      ]);

      setMetrics(metricsRes.data);
      setByType(byTypeRes.data);
      setRecentActivity(activityRes.data);
      setTasksMetrics(tasksMetricsRes.data);
      setAllDeadlines(deadlinesRes.data);
      setAllTasks(tasksRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const in7days = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

  // Computed: filtered deadlines
  const filteredDeadlines = useMemo(() => {
    return allDeadlines.filter(d => {
      if (deadlineFilter === 'Todos') return true;
      if (deadlineFilter === 'Pendientes') return d.estado === 'Pendiente';
      if (deadlineFilter === 'Por Vencer') return d.estado === 'Pendiente' && d.fecha_vencimiento >= today && d.fecha_vencimiento <= in7days;
      if (deadlineFilter === 'Realizados') return d.estado === 'Realizado';
      if (deadlineFilter === 'Vencidos') return d.estado === 'Vencido' || (d.estado === 'Pendiente' && d.fecha_vencimiento < today);
      return true;
    });
  }, [allDeadlines, deadlineFilter, today, in7days]);

  // Computed: deadlines grouped by client
  const deadlinesByClient = useMemo(() => {
    const groups = {};
    filteredDeadlines.forEach(d => {
      const name = d.client_name || 'Sin cliente';
      if (!groups[name]) groups[name] = { client_id: d.client_id, items: [] };
      groups[name].items.push(d);
    });
    // Sort by count desc
    return Object.entries(groups).sort((a, b) => b[1].items.length - a[1].items.length);
  }, [filteredDeadlines]);

  // Computed: filtered tasks
  const filteredTasks = useMemo(() => {
    return allTasks.filter(t => {
      if (taskFilter === 'Todos') return true;
      if (taskFilter === 'Pendientes') return t.estado === 'Pendiente';
      if (taskFilter === 'En Proceso') return t.estado === 'En proceso';
      if (taskFilter === 'Finalizadas') return t.estado === 'Finalizada';
      if (taskFilter === 'Vencidas') return (t.estado === 'Pendiente' || t.estado === 'En proceso') && t.fecha_limite < today;
      return true;
    });
  }, [allTasks, taskFilter, today]);

  // Computed: tasks grouped by client
  const tasksByClient = useMemo(() => {
    const groups = {};
    filteredTasks.forEach(t => {
      const name = t.client_name || 'Sin cliente';
      if (!groups[name]) groups[name] = { client_id: t.client_id, items: [] };
      groups[name].items.push(t);
    });
    return Object.entries(groups).sort((a, b) => b[1].items.length - a[1].items.length);
  }, [filteredTasks]);

  // Deadline filter counts
  const deadlineCounts = useMemo(() => {
    const pendientes = allDeadlines.filter(d => d.estado === 'Pendiente').length;
    const porVencer = allDeadlines.filter(d => d.estado === 'Pendiente' && d.fecha_vencimiento >= today && d.fecha_vencimiento <= in7days).length;
    const realizados = allDeadlines.filter(d => d.estado === 'Realizado').length;
    const vencidos = allDeadlines.filter(d => d.estado === 'Vencido' || (d.estado === 'Pendiente' && d.fecha_vencimiento < today)).length;
    return { Todos: allDeadlines.length, Pendientes: pendientes, 'Por Vencer': porVencer, Realizados: realizados, Vencidos: vencidos };
  }, [allDeadlines, today, in7days]);

  // Task filter counts
  const taskCounts = useMemo(() => {
    const pendientes = allTasks.filter(t => t.estado === 'Pendiente').length;
    const enProceso = allTasks.filter(t => t.estado === 'En proceso').length;
    const finalizadas = allTasks.filter(t => t.estado === 'Finalizada').length;
    const vencidas = allTasks.filter(t => (t.estado === 'Pendiente' || t.estado === 'En proceso') && t.fecha_limite < today).length;
    return { Todos: allTasks.length, Pendientes: pendientes, 'En Proceso': enProceso, Finalizadas: finalizadas, Vencidas: vencidas };
  }, [allTasks, today]);

  const toggleDeadlineClient = (clientName) => {
    setExpandedDeadlineClients(prev => ({ ...prev, [clientName]: !prev[clientName] }));
  };

  const toggleTaskClient = (clientName) => {
    setExpandedTaskClients(prev => ({ ...prev, [clientName]: !prev[clientName] }));
  };

  const getDaysBadgeColor = (days) => {
    if (days < 0) return 'bg-red-100 text-red-800';
    if (days < 3) return 'bg-red-100 text-red-800';
    if (days <= 7) return 'bg-indigo-100 text-indigo-800';
    return 'bg-emerald-100 text-emerald-800';
  };

  const getStatusBadge = (estado) => {
    const badges = {
      Pendiente: 'bg-indigo-100 text-indigo-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      Realizado: 'bg-emerald-100 text-emerald-800',
      Vencido: 'bg-red-100 text-red-800',
      Finalizada: 'bg-emerald-100 text-emerald-800',
    };
    return badges[estado] || 'bg-slate-100 text-slate-800';
  };

  const getTabStyle = (active, color) => {
    const base = 'px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap';
    if (active) return `${base} ${color} shadow-sm`;
    return `${base} bg-slate-100 text-slate-600 hover:bg-slate-200`;
  };

  const handleDeadlineClick = (deadline) => {
    setSelectedDeadline(deadline);
    setShowDeadlineModal(true);
  };

  const handleViewClient = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  const calcDaysRemaining = (fechaVencimiento) => {
    const deadline = new Date(fechaVencimiento + 'T00:00:00');
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.ceil((deadline - now) / 86400000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Cargando dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Dashboard
        </h1>
        <p className="text-sm text-slate-700 leading-relaxed mt-2">Vista general de la gestion tributaria</p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-[#3B82F6] transition-all"
          data-testid="metric-clients"
          onClick={() => navigate('/clients')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Clientes Activos</p>
              <p className="text-3xl font-bold text-[#3B82F6] mt-2">{metrics?.total_clients || 0}</p>
            </div>
            <TrendingUp className="w-7 h-7 text-[#3B82F6]" />
          </div>
        </div>
        <div
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-[#6366F1] transition-all"
          data-testid="metric-month"
          onClick={() => navigate('/deadlines')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Vencimientos Mes</p>
              <p className="text-3xl font-bold text-[#6366F1] mt-2">{metrics?.deadlines_this_month || 0}</p>
            </div>
            <Clock className="w-7 h-7 text-[#6366F1]" />
          </div>
        </div>
        <div
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-[#10B981] transition-all"
          data-testid="metric-completed"
          onClick={() => navigate('/deadlines')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Realizados Mes</p>
              <p className="text-3xl font-bold text-[#10B981] mt-2">{metrics?.completed_this_month || 0}</p>
            </div>
            <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
          </div>
        </div>
        <div
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-[#EF4444] transition-all"
          data-testid="metric-overdue"
          onClick={() => navigate('/deadlines')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Vencidos Sin Realizar</p>
              <p className="text-3xl font-bold text-[#EF4444] mt-2">{metrics?.overdue || 0}</p>
            </div>
            <AlertCircle className="w-7 h-7 text-[#EF4444]" />
          </div>
        </div>
      </div>

      {/* ===== INTERACTIVE DEADLINES SECTION ===== */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="deadlines-interactive">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ListFilter className="w-5 h-5 text-[#1F3864]" />
            <h2 className="text-xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>
              Vencimientos por Cliente
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="deadline-filter-tabs">
            {['Todos', 'Pendientes', 'Por Vencer', 'Realizados', 'Vencidos'].map(tab => (
              <button
                key={tab}
                data-testid={`deadline-tab-${tab.toLowerCase().replace(' ', '-')}`}
                onClick={() => { setDeadlineFilter(tab); setExpandedDeadlineClients({}); }}
                className={getTabStyle(
                  deadlineFilter === tab,
                  tab === 'Vencidos' ? 'bg-red-600 text-white' :
                  tab === 'Realizados' ? 'bg-emerald-600 text-white' :
                  tab === 'Por Vencer' ? 'bg-amber-500 text-white' :
                  tab === 'Pendientes' ? 'bg-indigo-600 text-white' :
                  'bg-[#1F3864] text-white'
                )}
              >
                {tab} ({deadlineCounts[tab] || 0})
              </button>
            ))}
          </div>
        </div>

        {deadlinesByClient.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No hay vencimientos para este filtro</p>
        ) : (
          <div className="space-y-1">
            {deadlinesByClient.map(([clientName, { client_id, items }]) => (
              <div key={clientName} className="border border-slate-100 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleDeadlineClient(clientName)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                  data-testid={`deadline-client-${client_id}`}
                >
                  <div className="flex items-center gap-3">
                    {expandedDeadlineClients[clientName] ?
                      <ChevronDown className="w-4 h-4 text-slate-500" /> :
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    }
                    <span className="text-sm font-semibold text-slate-800">{clientName}</span>
                  </div>
                  <span className="text-xs font-bold bg-[#1F3864] text-white px-2.5 py-1 rounded-full">{items.length}</span>
                </button>
                {expandedDeadlineClients[clientName] && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs font-semibold tracking-wider uppercase text-slate-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Tipo Impuesto</th>
                          <th className="px-4 py-2 text-left">Periodo</th>
                          <th className="px-4 py-2 text-left">Fecha Vencimiento</th>
                          <th className="px-4 py-2 text-left">Dias</th>
                          <th className="px-4 py-2 text-left">Estado</th>
                          <th className="px-4 py-2 text-left">Responsable</th>
                          <th className="px-4 py-2 text-center">Ver</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.sort((a, b) => a.fecha_vencimiento.localeCompare(b.fecha_vencimiento)).map(d => {
                          const days = calcDaysRemaining(d.fecha_vencimiento);
                          return (
                            <tr key={d._id} className="border-t border-slate-100 hover:bg-blue-50/40 transition-colors">
                              <td className="px-4 py-2 text-slate-700">{d.tipo_display || d.tipo_impuesto}</td>
                              <td className="px-4 py-2 text-slate-600">{d.periodo_gravable}</td>
                              <td className="px-4 py-2 text-slate-700 font-medium">{d.fecha_vencimiento}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDaysBadgeColor(days)}`}>
                                  {days < 0 ? `${Math.abs(days)}d atras` : `${days}d`}
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(d.estado)}`}>
                                  {d.estado}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-slate-600">{d.responsable_name || '-'}</td>
                              <td className="px-4 py-2 text-center">
                                <button onClick={() => handleDeadlineClick(d)} className="text-[#1F3864] hover:text-blue-800 transition-colors">
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===== INTERACTIVE TASKS SECTION ===== */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="tasks-interactive">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-[#1F3864]" />
            <h2 className="text-xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>
              Tareas por Cliente
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="task-filter-tabs">
            {['Todos', 'Pendientes', 'En Proceso', 'Finalizadas', 'Vencidas'].map(tab => (
              <button
                key={tab}
                data-testid={`task-tab-${tab.toLowerCase().replace(' ', '-')}`}
                onClick={() => { setTaskFilter(tab); setExpandedTaskClients({}); }}
                className={getTabStyle(
                  taskFilter === tab,
                  tab === 'Vencidas' ? 'bg-red-600 text-white' :
                  tab === 'Finalizadas' ? 'bg-emerald-600 text-white' :
                  tab === 'En Proceso' ? 'bg-blue-600 text-white' :
                  tab === 'Pendientes' ? 'bg-indigo-600 text-white' :
                  'bg-[#1F3864] text-white'
                )}
              >
                {tab} ({taskCounts[tab] || 0})
              </button>
            ))}
          </div>
        </div>

        {tasksByClient.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">No hay tareas para este filtro</p>
        ) : (
          <div className="space-y-1">
            {tasksByClient.map(([clientName, { client_id, items }]) => (
              <div key={clientName} className="border border-slate-100 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleTaskClient(clientName)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                  data-testid={`task-client-${client_id}`}
                >
                  <div className="flex items-center gap-3">
                    {expandedTaskClients[clientName] ?
                      <ChevronDown className="w-4 h-4 text-slate-500" /> :
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    }
                    <span className="text-sm font-semibold text-slate-800">{clientName}</span>
                  </div>
                  <span className="text-xs font-bold bg-[#1F3864] text-white px-2.5 py-1 rounded-full">{items.length}</span>
                </button>
                {expandedTaskClients[clientName] && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs font-semibold tracking-wider uppercase text-slate-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Titulo</th>
                          <th className="px-4 py-2 text-left">Prioridad</th>
                          <th className="px-4 py-2 text-left">Fecha Limite</th>
                          <th className="px-4 py-2 text-left">Estado</th>
                          <th className="px-4 py-2 text-center">Ver</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.sort((a, b) => (a.fecha_limite || '').localeCompare(b.fecha_limite || '')).map(t => {
                          const priorityColor = t.prioridad === 'Alta' ? 'bg-red-100 text-red-800' :
                            t.prioridad === 'Media' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700';
                          return (
                            <tr key={t._id} className="border-t border-slate-100 hover:bg-blue-50/40 transition-colors">
                              <td className="px-4 py-2 text-slate-700 font-medium">{t.titulo}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColor}`}>{t.prioridad}</span>
                              </td>
                              <td className="px-4 py-2 text-slate-700">{t.fecha_limite || '-'}</td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(t.estado)}`}>{t.estado}</span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <button onClick={() => handleViewClient(t.client_id)} className="text-[#1F3864] hover:text-blue-800 transition-colors">
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Two Column: By Type + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <h2 className="text-lg tracking-tight font-medium mb-4" style={{ fontFamily: 'Outfit' }}>
            Vencimientos por Tipo de Impuesto
          </h2>
          <div className="space-y-3">
            {byType.length === 0 ? (
              <p className="text-sm text-slate-500">No hay datos disponibles</p>
            ) : (
              byType.map((item) => (
                <div
                  key={item.tipo}
                  className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 px-2 rounded cursor-pointer transition-colors"
                  onClick={() => navigate('/deadlines')}
                >
                  <span className="text-sm text-slate-700">{item.tipo}</span>
                  <span className="text-sm font-semibold text-slate-900 bg-slate-100 px-2 py-1 rounded">{item.count}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <h2 className="text-lg tracking-tight font-medium mb-4" style={{ fontFamily: 'Outfit' }}>
            Actividad Reciente
          </h2>
          <div className="space-y-3">
            {recentActivity.length === 0 ? (
              <p className="text-sm text-slate-500">No hay actividad reciente</p>
            ) : (
              recentActivity.map((item) => (
                <div
                  key={item._id}
                  className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 px-2 rounded cursor-pointer transition-colors"
                  onClick={() => handleViewClient(item.client_id)}
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{item.client_name}</p>
                      <p className="text-xs text-slate-600">{item.tipo_display || item.tipo_impuesto} - {item.periodo_gravable}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-600">{item.responsable_name}</p>
                    <p className="text-xs text-slate-500">{item.fecha_realizacion}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Deadline Details Modal */}
      <Dialog open={showDeadlineModal} onOpenChange={setShowDeadlineModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalles del Vencimiento</DialogTitle>
          </DialogHeader>
          {selectedDeadline && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Cliente</p>
                  <p className="text-sm text-slate-900 mt-1 font-medium">{selectedDeadline.client_name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Estado</p>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusBadge(selectedDeadline.estado)}`}>
                    {selectedDeadline.estado}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Tipo de Impuesto</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedDeadline.tipo_impuesto}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Periodo Gravable</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedDeadline.periodo_gravable}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Fecha de Vencimiento</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedDeadline.fecha_vencimiento}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Responsable</p>
                  <p className="text-sm text-slate-900 mt-1">{selectedDeadline.responsable_name || '-'}</p>
                </div>
                {selectedDeadline.notas && (
                  <div className="col-span-2">
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Notas</p>
                    <p className="text-sm text-slate-900 mt-1">{selectedDeadline.notas}</p>
                  </div>
                )}
                {selectedDeadline.estado === 'Realizado' && selectedDeadline.fecha_realizacion && (
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Fecha de Realizacion</p>
                    <p className="text-sm text-slate-900 mt-1">{selectedDeadline.fecha_realizacion}</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => handleViewClient(selectedDeadline.client_id)}
                  className="px-4 py-2 bg-[#1F3864] hover:bg-[#172b4d] text-white rounded-md transition-colors text-sm"
                >
                  Ver Cliente
                </button>
                <button
                  onClick={() => setShowDeadlineModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md transition-colors text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
